(() => {
  const sectionSelector = '[data-hpn-premium-ingredients]';
  const trackSelector = '[data-hpn-premium-ingredients-track]';
  const arrowsSelector = '[data-hpn-premium-ingredients-arrows]';
  const prevSelector = '[data-hpn-premium-ingredients-prev]';
  const nextSelector = '[data-hpn-premium-ingredients-next]';
  const openSelector = '[data-hpn-premium-ingredients-open]';
  const ctaSelector = '.hpn-premium-ingredients__cta';
  const dialogSelector = '[data-hpn-premium-ingredients-dialog]';
  const panelSelector = '[data-hpn-premium-ingredients-panel]';
  const closeSelector = '[data-hpn-premium-ingredients-close]';
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  let activeDialog = null;
  let activeTrigger = null;
  let previousBodyOverflow = '';
  let closeTimer = null;

  const getTransitionDuration = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return 0;
    }

    return 180;
  };

  const getScrollAmount = (track) => {
    const firstIngredient = track.querySelector('.hpn-premium-ingredients__ingredient');

    if (!firstIngredient) {
      return track.clientWidth;
    }

    const styles = window.getComputedStyle(track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;

    return firstIngredient.getBoundingClientRect().width + gap;
  };

  const updateSection = (section) => {
    const track = section.querySelector(trackSelector);
    const arrows = section.querySelector(arrowsSelector);

    if (!track || !arrows) {
      return;
    }

    const isScrollable = track.scrollWidth > track.clientWidth + 2;

    arrows.hidden = !isScrollable;
    section.classList.toggle('hpn-premium-ingredients--is-scrollable', isScrollable);
  };

  const getFocusableElements = (dialog) => Array.from(dialog.querySelectorAll(focusableSelector))
    .filter((element) => element.offsetParent !== null || element === document.activeElement);

  const lockBodyScroll = () => {
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  };

  const unlockBodyScroll = () => {
    document.body.style.overflow = previousBodyOverflow;
  };

  const finishCloseDialog = (dialog, trigger, shouldReturnFocus) => {
    dialog.hidden = true;
    dialog.classList.remove('hpn-premium-ingredients__dialog--is-closing');
    unlockBodyScroll();

    if (shouldReturnFocus && trigger) {
      trigger.focus();
    }
  };

  const closeDialog = ({ shouldReturnFocus = true, immediate = false } = {}) => {
    if (!activeDialog) {
      return;
    }

    const dialog = activeDialog;
    const trigger = activeTrigger;

    window.clearTimeout(closeTimer);
    dialog.classList.remove('hpn-premium-ingredients__dialog--is-open');
    dialog.classList.add('hpn-premium-ingredients__dialog--is-closing');
    activeDialog = null;
    activeTrigger = null;

    if (immediate) {
      finishCloseDialog(dialog, trigger, false);
      return;
    }

    closeTimer = window.setTimeout(() => {
      finishCloseDialog(dialog, trigger, shouldReturnFocus);
    }, getTransitionDuration());
  };

  const openDialog = (dialog, trigger) => {
    if (!dialog) {
      return;
    }

    if (activeDialog) {
      closeDialog({ shouldReturnFocus: false, immediate: true });
    }

    window.clearTimeout(closeTimer);
    activeDialog = dialog;
    activeTrigger = trigger;
    dialog.classList.remove('hpn-premium-ingredients__dialog--is-closing');
    dialog.hidden = false;
    lockBodyScroll();

    window.requestAnimationFrame(() => {
      dialog.classList.add('hpn-premium-ingredients__dialog--is-open');
    });

    const closeButton = dialog.querySelector(closeSelector);

    if (closeButton) {
      closeButton.focus();
    } else {
      dialog.setAttribute('tabindex', '-1');
      dialog.focus();
    }
  };

  const handleDialogKeydown = (event) => {
    if (!activeDialog) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDialog();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = getFocusableElements(activeDialog);

    if (!focusableElements.length) {
      event.preventDefault();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  const bindDialogs = (section) => {
    section.querySelectorAll(openSelector).forEach((trigger) => {
      if (trigger.dataset.hpnPremiumIngredientsPopupBound === 'true') {
        return;
      }

      trigger.dataset.hpnPremiumIngredientsPopupBound = 'true';

      trigger.addEventListener('click', () => {
        const dialogId = trigger.getAttribute('data-hpn-premium-ingredients-open');
        const dialog = document.getElementById(dialogId);

        if (dialog && section.contains(dialog)) {
          openDialog(dialog, trigger);
        }
      });
    });

    section.querySelectorAll(ctaSelector).forEach((cta) => {
      if (cta.dataset.hpnPremiumIngredientsCtaBound === 'true') {
        return;
      }

      cta.dataset.hpnPremiumIngredientsCtaBound = 'true';

      cta.addEventListener('click', (event) => {
        const firstTrigger = section.querySelector(openSelector);

        if (!firstTrigger) {
          return;
        }

        const dialogId = firstTrigger.getAttribute('data-hpn-premium-ingredients-open');
        const dialog = dialogId ? document.getElementById(dialogId) : null;

        if (dialog && section.contains(dialog)) {
          event.preventDefault();
          openDialog(dialog, cta);
        }
      });
    });

    section.querySelectorAll(dialogSelector).forEach((dialog) => {
      if (dialog.dataset.hpnPremiumIngredientsDialogBound === 'true') {
        return;
      }

      dialog.dataset.hpnPremiumIngredientsDialogBound = 'true';

      dialog.addEventListener('click', (event) => {
        const panel = dialog.querySelector(panelSelector);

        if (!panel || !panel.contains(event.target)) {
          closeDialog();
        }
      });

      dialog.querySelectorAll(closeSelector).forEach((closeButton) => {
        closeButton.addEventListener('click', closeDialog);
      });
    });
  };

  const initSection = (section) => {
    if (section.dataset.hpnPremiumIngredientsInitialized === 'true') {
      updateSection(section);
      return;
    }

    const track = section.querySelector(trackSelector);
    const prev = section.querySelector(prevSelector);
    const next = section.querySelector(nextSelector);

    if (!track || !prev || !next) {
      return;
    }

    section.dataset.hpnPremiumIngredientsInitialized = 'true';

    prev.addEventListener('click', () => {
      track.scrollBy({
        left: -getScrollAmount(track),
        behavior: 'smooth',
      });
    });

    next.addEventListener('click', () => {
      track.scrollBy({
        left: getScrollAmount(track),
        behavior: 'smooth',
      });
    });

    bindDialogs(section);
    updateSection(section);

    window.addEventListener('resize', () => updateSection(section), { passive: true });
  };

  const initAll = () => {
    document.querySelectorAll(sectionSelector).forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('keydown', handleDialogKeydown);

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.matches(sectionSelector)) {
      initSection(event.target);
      return;
    }

    event.target.querySelectorAll(sectionSelector).forEach(initSection);
  });
})();
